import {
  EsignPagesViewer,
  type EsignPagesViewerControls,
} from "@/pages/esign/components/EsignPagesViewer.tsx";
import { ESIGN_PAGE_WIDTH } from "@/pages/esign/esign.config.ts";
import { EsignDeclineModal } from "@/pages/esign/widgets/EsignDeclineModal.tsx";
import { useEsignRequestSourceUrl } from "@/pages/esign/use-esign-request-source.hook.ts";
import { useEsignRequest, useEsignRequestSign } from "@/queries/use-esign.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  Button,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EsignAcknowledgeModalProps extends ModalProps {
  requestId: string;
}

export function EsignAcknowledgeModal({ requestId, ...props }: EsignAcknowledgeModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { id } = props;

  const { data: request } = useEsignRequest(requestId);
  const {
    url: documentUrl,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
  } = useEsignRequestSourceUrl(requestId);
  const { mutate: signRequest, isPending } = useEsignRequestSign();

  useEffect(() => {
    if (isDocumentError) close(id);
  }, [isDocumentError, close, id]);

  const [hasReadToEnd, setHasReadToEnd] = useState(false);
  const handleReachedEnd = useCallback(() => setHasReadToEnd(true), []);
  const controlsRef = useRef<EsignPagesViewerControls | null>(null);

  const canConfirm = hasReadToEnd && !isPending;
  const isLoading = !request || isDocumentLoading;

  const handleButtonClick = () => {
    if (isPending) return;
    if (!hasReadToEnd) {
      controlsRef.current?.scrollToNext();
      return;
    }
    signRequest(
      { requestId },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.esign.acknowledged, {
            [analyticProperties.id]: requestId,
          });
          close(id);
        },
      }
    );
  };

  const handleDecline = () => {
    openModal(EsignDeclineModal, { requestId, onDeclined: () => close(id) });
  };

  return (
    <Modal
      {...props}
      title={t("core:esign_read_and_agree")}
      caption={request?.document_title}
      variant={"middle"}
      layout={"stretch"}
      size={"full"}
      withCloser={true}
      actions={
        <Stack
          direction={"horizontal"}
          items={"center"}
          justify={"end"}
          gap={"lg"}
          className={"w-full"}
        >
          <div className={"mr-auto"}>
            <Button size={"lg"} variant={"plain"} disabled={isPending} onClick={handleDecline}>
              {t("core:esign_decline")}
            </Button>
          </div>
          {!hasReadToEnd ? (
            <Text className={"text-secondary text-right"}>{t("core:esign_read_to_end_hint")}</Text>
          ) : (
            <Text className={"text-secondary max-w-120 text-right"}>
              {request?.intent_statement}
            </Text>
          )}
          <Tooltip>
            <TooltipTrigger asChild={true}>
              <Button
                size={"lg"}
                className={canConfirm ? undefined : "opacity-60"}
                disabled={isPending}
                isLoading={isPending}
                onClick={handleButtonClick}
              >
                {hasReadToEnd
                  ? t("core:esign_i_have_read_and_acknowledge")
                  : t("core:esign_continue_reading")}
              </Button>
            </TooltipTrigger>
            {!hasReadToEnd && (
              <TooltipContent>
                <Text className={"text-light"}>{t("core:esign_read_to_end_hint")}</Text>
              </TooltipContent>
            )}
          </Tooltip>
        </Stack>
      }
    >
      {isLoading || !documentUrl ? (
        <Skeleton className={"mx-auto h-215 w-full max-w-170"} />
      ) : (
        <EsignPagesViewer
          src={documentUrl}
          pageNavigationPlacement={"left"}
          maxRenderWidth={ESIGN_PAGE_WIDTH}
          onReachedEnd={handleReachedEnd}
          controlsRef={controlsRef}
        />
      )}
    </Modal>
  );
}
